import {
  useAppDispatch,
  useAppSelector,
} from "../../../redux-store/CustomReduxHooks";
import { T_CustomerLobDetailObj } from "../../Feature_Login/Login_Types";
import { T_ApiResponse } from "../../../globalTypes/GlobalTypes";
import { LiLobCode } from "../../../utils/constants";
import { useCallback, useEffect, useReducer } from "react";
import {
  useLazyFetchDashboardDetailsQuery,
  useLazyFetchPolicyDetailsQuery,
} from "../LifeInsurance_Api";
import {
  setLiDashboardDetails,
  setLiDashboardFundDetails,
  setLiPolicyDetails,
  setLiSecondaryDashboardFundDetails,
  setSecondaryLiDashboardDetails,
} from "../LifeInsurance_Slice";
import { T_LIDashboardDetails } from "../LifeInsurance_Types";
import { isArray, isEmpty, uniqBy } from "lodash";
import { getPolicyStatus } from "../../../utils/conversionUtils";
import { encryptString } from "../../../utils/helper";
import { API_STATUS_CODE } from "../../../utils/ApiEndpoints";
import { AxiosError } from "axios";
import {
  LI_HandlingReducerAction,
  LI_ReduxInitialState,
} from "./Li_ReduxActionHandler";
import { T_LocalLiBaseData } from "./Li_BaseData_Types";
import { setError } from "../../../redux-store/reducers/CsErrorSlice";
export const UseLiBaseDataLoader = (
  loadFor: "primary" | "secondary" = "primary",
  policyNumber: string = "",
  isHookCalled: boolean = true
): T_LocalLiBaseData => {
  const [localState, updateLocalState] = useReducer(
    LI_HandlingReducerAction,
    LI_ReduxInitialState
  );
  const dispatch = useAppDispatch();
  const userDetails = useAppSelector((state) => state.CsAppSlice.userDetails);
  const policyDetails = useAppSelector(
    (st) => st.LifeInsuranceSlice.policyDetails
  );
  const primaryUserDashboardDetails = useAppSelector(
    (st) => st.LifeInsuranceSlice.dashboardDetails
  );
  const customerAndLobDetails = userDetails?.customerAndLobDetails;
  const UserLiLobId = customerAndLobDetails?.filter(
    (item: T_CustomerLobDetailObj) =>
      item.LOB === LiLobCode && item.PRIMARY_SECONDARY_USER === loadFor
  );
  const [
    fetchPolicyDetails,
    { error: errInPolicyDetailsApi, isFetching: isLoadingPolicyDetails },
  ] = useLazyFetchPolicyDetailsQuery();
  const [
    fetchDashboardDetailsApi,
    { error: errInDashboardDetailsApi, isFetching: isLoadingDashboardDetails },
  ] = useLazyFetchDashboardDetailsQuery();

  const checkAndinitApiFlow = useCallback(() => {
    /** FN for Loading LI Dashboard Details */
    if (isEmpty(UserLiLobId)) {
      updateLocalState({
        type: "error",
        payload: Error("Li client id not present"),
      });
    } else if (isEmpty(primaryUserDashboardDetails)) {
      updateLocalState({ type: "fetching", payload: "" });
      fetchPrimaryUserDashboardDetails();
    } else if (
      !isEmpty(policyNumber) &&
      isEmpty(
        policyDetails?.filter((f) => f?.PolicyDetails?.POL_ID === policyNumber)
      )
    ) {
      checkAndInitPolicyDetails();
    } else {
      updateLocalState({ type: "success", payload: "" });
    }
  }, [UserLiLobId, primaryUserDashboardDetails]);

  const checkAndInitPolicyDetails = useCallback(() => {
    if (isLoadingPolicyDetails) {
      return;
    }
    updateLocalState({ type: "fetching", payload: "" });
    fetchPolicyDetails(policyNumber, true).then((res) => {
      if (res.data?.code === API_STATUS_CODE.SUCCESS) {
        if (isEmpty(res.data?.payload)) {
          updateLocalState({ type: "error", payload: Error("Empty List") });
        } else {
          dispatch(setLiPolicyDetails([res.data?.payload]));
        }
      }
    });
  }, [policyNumber, policyDetails, isLoadingPolicyDetails]);

  /** FN for Loading LI Dashboard Details */
  const fetchPrimaryUserDashboardDetails = async () => {
    if (isLoadingDashboardDetails) {
      return;
    }
    let primaryUserPromises = uniqBy(UserLiLobId, "LOBID")?.map(
      (item: T_CustomerLobDetailObj) => {
        return fetchDashboardDetailsApi(item.LOBID, true);
      }
    );
    let ApiListPromises = await Promise.all(primaryUserPromises);

    let transformedUserResults;
    if (loadFor === "primary") {
      /** Process Primary policies and save to redux */
      transformedUserResults = ApiListPromises.map((result) => {
        return isEmpty(result.data?.payload.DASHBOARDdDETAILS)
          ? []
          : result?.data?.payload?.DASHBOARDdDETAILS.filter(
              (item: T_LIDashboardDetails) =>
                getPolicyStatus(item.POLICY_STATUS)
            );
      });
      const transFormedPrimaryFundDetails = ApiListPromises.map((item) => {
        return isArray(item?.data?.payload.FUNDDETAILS)
          ? item?.data?.payload.FUNDDETAILS
          : [];
      });
      const transformedResult = transformedUserResults.flat(Infinity);
      if (isEmpty(transformedResult)) {
        updateLocalState({
          type: "error",
          payload: Error("Li dashboard api failed"),
        });
        dispatch(
          setError({
            error: new Error("Error"),
            message: "fetch dashboard details API failed",
          })
        );
        return;
      }
      dispatch(setLiDashboardDetails(transformedUserResults.flat(Infinity)));
      dispatch(
        setLiDashboardFundDetails(transFormedPrimaryFundDetails.flat(Infinity))
      );
    } else {
      /** Process Secondary policies and save to redux */
      transformedUserResults = ApiListPromises.map((result) => {
        return isEmpty(result.data?.payload.DASHBOARDdDETAILS)
          ? []
          : result?.data?.payload?.DASHBOARDdDETAILS.filter(
              (item: T_LIDashboardDetails) => {
                let policyNumberArray = UserLiLobId?.map(
                  (el) => el.HOLDING_IDENTIFIER_ONE
                );
                policyNumberArray?.includes(item.POLICY_NUMBER) &&
                  getPolicyStatus(item.POLICY_STATUS);
              }
            );
      });
      const transFormedSecondaryFundDetails = ApiListPromises.map((item) => {
        return isArray(item?.data?.payload.FUNDDETAILS)
          ? item?.data?.payload.FUNDDETAILS
          : [];
      });
      dispatch(
        setSecondaryLiDashboardDetails(transformedUserResults.flat(Infinity))
      );
      dispatch(
        setLiSecondaryDashboardFundDetails(
          transFormedSecondaryFundDetails.flat(Infinity)
        )
      );
    }
  };

  useEffect(() => {
    // if (!isEmpty(policyNumber)) {
    if (isHookCalled) {
      checkAndinitApiFlow();
    }
    // }
  }, [primaryUserDashboardDetails, policyDetails, policyNumber, isHookCalled]);

  useEffect(() => {
    if (errInDashboardDetailsApi || errInPolicyDetailsApi) {
      updateLocalState({
        type: "error",
        payload: (errInDashboardDetailsApi || Error("api failed")) as Error,
      });
    }
  }, [errInPolicyDetailsApi, errInDashboardDetailsApi]);

  return localState;
};
